import React, { useState, useEffect, useRef } from "react";
import { API_BASE } from "../api/config";
import { FaSearch } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";

function OrderBySelf() {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [kotItems, setKotItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [fadeOut, setFadeOut] = useState(false);

  const navigate = useNavigate();
  const { branch, tableName } = useParams();
  const searchRef = useRef(null); // ✅ reference for the search box
  
   const [showKotDetails, setShowKotDetails] = useState(false);
  const [mobileKotOpen, setMobileKotOpen] = useState(false);

  // ✅ Fetch tables for this branch
  const fetchTables = async () => {
    try {
      const response = await fetch(
        `${API_BASE}?key=dzital_key&pswd=Hibujji@123&table=get_active_table&transac=get&condition=get_status_active_tables&branch=${encodeURIComponent(
          branch
        )}`
      );
      const data = await response.json();
      const mappedTables = data.map((t, idx) => ({
        id: idx + 1,
        name: t.table_name,
        seats: 4,
        status: t.table_booked_status === "1" ? "Occupied" : "Available",
      }));
      setTables(mappedTables);

      // ✅ Auto-select table if matched
      if (tableName) {
        const match = mappedTables.find(
          (tbl) => tbl.name.toLowerCase() === tableName.toLowerCase()
        );
        if (match) setSelectedTable(match);
      }
    } catch (error) {
      console.error("Error fetching tables:", error);
    }
  };

  // ✅ Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(
          `${API_BASE}?key=dzital_key&pswd=Hibujji@123&table=groups&transac=get&condition=get_active_groups&branch=${encodeURIComponent(
            branch
          )}`
        );
        const data = await res.json();
        setCategories(data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, [branch]);

  // ✅ Fetch items
  useEffect(() => {
    const fetchItems = async () => {
      try {
        const res = await fetch(
          `${API_BASE}?key=dzital_key&pswd=Hibujji@123&table=items&transac=get&condition=get_active_items&branch=${encodeURIComponent(
            branch
          )}`
        );
        const data = await res.json();
        setItems(data);
      } catch (error) {
        console.error("Error fetching items:", error);
      }
    };
    fetchItems();
  }, [branch]);

  // ✅ Fetch tables when params change
  useEffect(() => {
    fetchTables();
  }, [branch, tableName]);

  // ✅ Auto focus search box when category changes
  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, [selectedCategory]);

  // ✅ Filter items
  const filteredItems = searchTerm
    ? items.filter((item) =>
        item.item_name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : selectedCategory
    ? items.filter((item) => item.group === selectedCategory.item_name)
    : [];

  // ----------------- KOT Functions -----------------
  const handleSelectCategory = (category) => {
    setSelectedCategory(category);
    setSearchTerm("");
    setSelectedItem(null);
  };

  const handleAddItem = (item) => {
    setSelectedItem(item.id);
    const existing = kotItems.find((i) => i.id === item.id);
    if (existing) {
      setKotItems(
        kotItems.map((i) =>
          i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
        )
      );
    } else {
      setKotItems([
        ...kotItems,
        {
          ...item,
          quantity: 1,
          name: item.item_name,
          price: item.item_price,
          group_name: item.group,
        },
      ]);
    }

    // ✅ Clear search and refocus cursor in search box
    setSearchTerm("");
    if (searchRef.current) {
      searchRef.current.focus();
    }
  };

  const handleIncrease = (item) => {
    setKotItems(
      kotItems.map((i) =>
        i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
      )
    );
  };

  const handleDecrease = (item) => {
    setKotItems(
      kotItems
        .map((i) =>
          i.id === item.id ? { ...i, quantity: i.quantity - 1 } : i
        )
        .filter((i) => i.quantity > 0)
    );
  };

  const handleFinalizeKOT = async () => {
    if (!selectedTable || kotItems.length === 0) {
      alert("Select table and add items first!");
      return;
    }

    try {
      const payload = kotItems.map((item) => ({
        table_name: selectedTable.name,
        item_name: item.name,
        item_qty: item.quantity,
        group_name: item.group_name,
        item_price: item.price,
        branch: branch,
      }));

      setProcessing(true);

      await fetch(
        `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=insert_kot&branch=${encodeURIComponent(
          branch
        )}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      // ✅ Reset UI
      setSelectedCategory(null);
      setKotItems([]);
      setSearchTerm("");
      setSelectedItem(null);
      fetchTables();

      // ✅ Success toast animation
      setSuccessMessage("KOT placed successfully!");
      setFadeOut(false);
      setTimeout(() => setFadeOut(true), 1000);
      setTimeout(() => {
        setProcessing(false);
        setSuccessMessage("");
        navigate(`/track-my-kots/${branch}/${selectedTable.name}`);
      }, 2000);
    } catch (error) {
      console.error("Error sending KOT to server:", error);
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-gradient-to-br from-black via-blue-950 to-black text-white p-4 gap-4 relative">
      {/* ✅ Processing Overlay */}
      {processing && (
        <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-blue-950 text-white px-6 py-4 rounded-lg shadow-lg text-center animate-pulse">
            <h2 className="text-xl font-bold mb-2">Processing...</h2>
            <p className="text-gray-300">Please wait a moment.</p>
          </div>
        </div>
      )}

      {/* ✅ Success Toast */}
      {successMessage && (
        <div
          className={`absolute top-4 right-4 bg-green-600 text-white px-4 py-2 rounded shadow-lg transition-opacity duration-700 ${
            fadeOut ? "opacity-0" : "opacity-100"
          }`}
        >
          {successMessage}
        </div>
      )}

      {/* Left Panel */}
      <div className="flex-1 flex flex-col gap-4">
        {selectedTable ? (
          <>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-bold">Categories</h2>
              <button
                className="bg-blue-700 hover:bg-blue-800 px-3 py-1 rounded text-sm font-semibold"
                onClick={() =>
                  navigate(`/track-my-kots/${branch}/${selectedTable.name}`)
                }
              >
                Track My Order
              </button>
            </div>

            {/* Categories Grid */}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 overflow-y-auto max-h-[150px]">
              {categories.map((cat) => {
                const count = items.filter(
                  (item) => item.group === cat.item_name
                ).length;
                return (
                  <button
                    key={cat.id}
                    className={`flex flex-col items-center p-2 rounded text-sm font-semibold transition ${
                      selectedCategory?.item_name === cat.item_name
                        ? "bg-black text-blue-400 border border-blue-500"
                        : "bg-blue-950 text-white hover:bg-blue-800"
                    }`}
                    onClick={() => handleSelectCategory(cat)}
                  >
                    <span>{cat.item_name}</span>
                    <span className="text-xs text-gray-300 mt-1">
                      ({count})
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Search Bar */}
            <div className="relative mb-3">
              <input
                ref={searchRef}
                type="text"
                placeholder="Search items..."
                className="w-full pl-7 pr-2 p-2 rounded bg-black/70 text-white border border-gray-700"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FaSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
            </div>
			
			{kotItems.length > 0 && (
			    <div className="md:hidden">
			  <button
                className="px-3 py-1 bg-orange-700 rounded hover:bg-orange-800 w-full md:w-auto"
                onClick={() => setShowKotDetails(true)}
              >
                Show Order - {kotItems.reduce((acc, i) => acc + i.quantity, 0)}{" "}
{kotItems.reduce((acc, i) => acc + i.quantity, 0) === 1 ? "Item" : "Items"}
              </button>
			  </div>
			)}

            {/* Items Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 overflow-y-auto max-h-[calc(100vh-300px)]">
              {filteredItems.map((item) => {
                const isSelected = selectedItem === item.id;
                return (
                  <div
                    key={item.id}
                    className={`flex flex-col items-center justify-center p-2 rounded shadow cursor-pointer transition min-h-[70px] ${
                      isSelected
                        ? "bg-black text-blue-400 border border-blue-500"
                        : "bg-blue-950 text-white hover:bg-blue-800"
                    }`}
                    onClick={() => handleAddItem(item)}
                  >
                    <h3 className="font-semibold text-center text-sm">
                      {item.item_name}
                    </h3>
                    <span className="text-xs">
                      ₹{parseFloat(item.item_price).toFixed(2)}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <h2 className="text-center text-xl font-bold text-red-400">
            Invalid or unavailable table: {tableName}
          </h2>
        )}
      </div>

      {/* Right Panel - KOT */}
   <div className="hidden md:block w-2/5 bg-black/70 p-4 rounded-lg shadow-md h-fit max-h-[calc(100vh-50px)] overflow-y-auto">
        <h2 className="text-xl font-bold mb-3">
          {selectedTable ? `KOT - ${selectedTable.name}` : "KOT"}
        </h2>
        {kotItems.length === 0 ? (
          <p className="text-gray-300">No items added yet.</p>
        ) : (
          <>
            {kotItems.map((item) => (
              <div
                key={item.id}
                className="flex justify-between items-center mb-2 bg-blue-950/70 p-3 rounded"
              >
                <span>{item.name}</span>
                <div className="flex items-center gap-2">
                  <button
                    className="px-2 bg-red-600 rounded"
                    onClick={() => handleDecrease(item)}
                  >
                    -
                  </button>
                  <span>{item.quantity}</span>
                  <button
                    className="px-2 bg-green-600 rounded"
                    onClick={() => handleIncrease(item)}
                  >
                    +
                  </button>
                </div>
              </div>
            ))}
            <hr className="my-3 border-gray-600" />
            <div className="flex justify-between font-bold mb-3">
              <span>Total Items:</span>
              <span>{kotItems.reduce((acc, i) => acc + i.quantity, 0)}</span>
            </div>
            <button
              onClick={handleFinalizeKOT}
              className="w-full px-4 py-2 bg-blue-700 font-bold rounded hover:bg-blue-800"
            >
              Finalize & Go to Track KOTs 
            </button>
          </>
        )}
      </div>
	  
	  {/* Mobile Slide-Up KOT Panel */}
  {showKotDetails && (
    <div className="fixed inset-0 bg-black/80 flex flex-col justify-end z-50">
      <div className="bg-gray-900 p-4 rounded-t-2xl max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-bold">KOT Details</h2>
          <button
            className="text-gray-300 hover:text-white"
            onClick={() => setShowKotDetails(false)}
          >
            ✕
          </button>
        </div>
        {kotItems.map((item) => (
          <div
            key={item.id}
            className="flex justify-between items-center mb-2 bg-blue-950/70 p-3 rounded"
          >
            <span>{item.name}</span>
            <div className="flex items-center gap-2">
              <button
                className="px-2 bg-red-600 rounded"
                onClick={() => handleDecrease(item)}
              >
                -
              </button>
              <span>{item.quantity}</span>
              <button
                className="px-2 bg-green-600 rounded"
                onClick={() => handleIncrease(item)}
              >
                +
              </button>
            </div>
          </div>
        ))}
        <hr className="my-3 border-gray-600" />
        <div className="flex justify-between font-bold mb-3">
          <span>Total Items:</span>
          <span>
            {kotItems.reduce((acc, i) => acc + i.quantity, 0)}
          </span>
        </div>
        <button
          onClick={handleFinalizeKOT}
          disabled={processing}
          className={`w-full px-4 py-2 font-bold rounded ${
            processing
              ? "bg-gray-600 cursor-not-allowed"
              : "bg-blue-700 hover:bg-blue-800"
          }`}
        >
          {processing ? "Processing..." : "Finalize & Print KOT"}
        </button>
      </div>
    </div>
  )}
	  
    </div>
  );
}

export default OrderBySelf;
